import { useMemo } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import { InfoCard } from '@backstage/core-components';
import {
  boschBlue,
  boschGray,
} from '../../../modules/theme/boschColors';
import { useApiData } from './useApiData';
import { ApiStatusBar } from './ApiStatusBar';

const useStyles = makeStyles(theme => ({
  /*
   * Monospaced JSON dump. We pin `maxHeight` so a 5000-line response
   * doesn't blow out the page layout — it scrolls inside the card
   * instead, keeping the rest of the page accessible.
   */
  pre: {
    margin: 0,
    padding: theme.spacing(1.5),
    backgroundColor: '#0F1B2D',
    color: '#E8EEF7',
    fontFamily:
      'ui-monospace, "Cascadia Code", Consolas, "Roboto Mono", monospace',
    fontSize: '0.78rem',
    lineHeight: 1.55,
    maxHeight: 480,
    overflow: 'auto',
    borderRadius: 4,
  },
  empty: {
    padding: theme.spacing(2),
    color: boschGray[40],
    fontStyle: 'italic',
    fontSize: '0.85rem',
  },
  /* Header chip showing which path was selected — gives the admin
   * a quick visual confirmation when the path doesn't resolve. */
  pathChip: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 4,
    backgroundColor: boschBlue[95],
    color: boschBlue[40],
    fontFamily:
      'ui-monospace, "Cascadia Code", Consolas, "Roboto Mono", monospace',
    fontSize: '0.7rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    border: `1px solid ${boschBlue[90]}`,
  },
}));

export type ApiJsonViewerProps = {
  title?: string;
  url?: string;
  inlineJson?: string;
  path?: string;
  refreshSeconds?: number;
};

/**
 * Formatted JSON inspector. Primary use cases:
 *
 *   1. Debugging an API integration BEFORE wiring it to a stat card
 *      / chart — drop this widget, paste the URL, see the shape.
 *   2. Showing example payloads on technical docs pages ("here's
 *      what this endpoint returns").
 *   3. Embedding a fixed JSON config snippet (e.g. example webhook
 *      payload) directly via `inlineJson`.
 *
 * Why not just point users at the browser's devtools network tab:
 *   - The widget shows the data as the SAME user permissions /
 *     proxy / auth setup that the rest of their dashboard widgets
 *     will see. Devtools shows the same call but from outside the
 *     Backstage auth context, so paths that work here might 401
 *     externally.
 *   - It's persisted in the catalog YAML, so onboarding teammates
 *     can read the page and see real example data without poking
 *     APIs themselves.
 */
export const ApiJsonViewer = ({
  title,
  url,
  inlineJson,
  path,
  refreshSeconds,
}: ApiJsonViewerProps) => {
  const classes = useStyles();
  const { data, loading, error, lastUpdatedAt, refetch } = useApiData({
    url,
    inlineJson,
    path,
    refreshSeconds,
  });

  const pretty = useMemo(() => {
    if (data === undefined) return undefined;
    try {
      // 2-space indent reads well at this font size; 4 spaces wastes
      // horizontal room on a 1/2-width tile.
      return JSON.stringify(data, null, 2);
    } catch {
      // Circular structures, BigInts, etc. — fall back to a short
      // marker so we never break the widget render.
      return '<unserializable response>';
    }
  }, [data]);

  return (
    <InfoCard title={title ?? 'API response'} noPadding>
      <div style={{ padding: 16 }}>
        {path && (
          <div className={classes.pathChip}>path: {path}</div>
        )}
        {!url && !inlineJson && (
          <Typography className={classes.empty}>
            Provide either a URL (e.g. <code>/api/proxy/&lt;name&gt;/path</code>)
            or paste an example JSON payload via <code>inlineJson</code>.
          </Typography>
        )}
        {pretty !== undefined && <pre className={classes.pre}>{pretty}</pre>}
        {data === undefined && (url || inlineJson) && !loading && !error && (
          <Typography className={classes.empty}>
            Path <code>{path || '(root)'}</code> did not resolve to any value
            in the response.
          </Typography>
        )}
      </div>
      <ApiStatusBar
        loading={loading}
        error={error}
        lastUpdatedAt={lastUpdatedAt}
        onRefresh={url ? refetch : undefined}
        modeLabel={url ? 'Live API' : 'Inline JSON'}
      />
    </InfoCard>
  );
};
